import { buildTeamEntryMap, type TeamEntry } from '@/app/[locale]/event/[eventId]/libs/teamLabels';
import { isCompletedGame } from '@/app/[locale]/match/libs/retirement';
import { getLuckyLoserPlayerIds } from '@/draw/lucky-loser';
import { activeSeedingRegistrants } from '@/draw/seedings';
import { tryResolveAppState } from '@/lib/scorecard/resolve';
import { playerModelV2 } from '@/models/playerModelV2';

/**
 * Pure projection from the draw model's output to the app's draw wire shapes.
 *
 * The two `/api/app/events/{eventId}/draws…` routes hold no draw logic of their
 * own: they gate, read through `drawModel`, and hand the result to this module.
 * Nothing here recomputes standings, seeding order or bracket structure — those
 * come out of `drawModel.getCompleteDrawData` (`src/models/drawModel.ts:1136`),
 * which itself delegates standings to `@/draw/core/standingsCalculator`. This
 * file only renames keys, nests rows and applies the app's visibility rules, so
 * there is one implementation of the draw, not two.
 *
 * The input types below are deliberately NARROWER than the model's row types:
 * they name exactly the columns the wire reads. The model's richer rows are
 * structurally assignable to them, so a producer key that disappears becomes a
 * compile error at the route's call site rather than an `undefined` on the wire.
 */

// ---------------------------------------------------------------------------
// Draw visibility
// ---------------------------------------------------------------------------

/**
 * `draw.status` runs `draft → seeded → generated → published → active →
 * completed` (`supabase/migrations/20250908023749_tournament_draw_schema.sql:35`
 * and its column comment at :338). Only the last three are a real, public draw;
 * the first three are an admin's work in progress — a bracket that has been
 * generated but not published can still be regenerated with different players.
 *
 * The app therefore omits them from the list and 404s them in detail. There is
 * no shared draw-visibility helper in the codebase to reuse, and the two web
 * surfaces disagree with each other: the public draw list filters
 * `status !== 'draft'` (`sortPublicDraws`, `drawListPresentation.ts`) while the public draw detail only
 * fetches when `status === 'published'` (`DrawViewClient.tsx`, the
 * `if (draw.status === 'published')` guard), which would hide a finished draw.
 * This rule is the intersection that is right for both.
 */
const PUBLIC_DRAW_STATUSES: readonly string[] = ['published', 'active', 'completed'];

/** The draw's matches exist (published / active / completed); the public rule's statuses. */
export function isDrawBuilt(draw: { status: string | null | undefined }): boolean {
  return draw.status != null && PUBLIC_DRAW_STATUSES.includes(draw.status);
}

/** The one draw-visibility rule: a real draw (published / active / completed) the organiser has
 *  not hidden. A row without the flag (null) reads as public. */
export function isDrawPubliclyVisible(draw: {
  status: string | null | undefined;
  hidden_from_public?: boolean | null;
}): boolean {
  return (
    draw.status != null && PUBLIC_DRAW_STATUSES.includes(draw.status) && !draw.hidden_from_public
  );
}

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

/** One row of `drawModel.getDrawListForEvent` (src/models/drawModel.ts:1314). */
export type DrawListRow = {
  id: string;
  name: string;
  status: string;
  draw_type: string;
  format_type: string;
  draw_size: number;
  created_at: string;
  hidden_from_public?: boolean | null;
  event_group: { id: string; name: string; sort_order: number | null };
};

export type AppDrawListItem = {
  id: string;
  name: string;
  status: string;
  draw_type: string;
  format_type: string;
  draw_size: number;
  division: { id: string; name: string; sort_order: number | null };
};

/**
 * Public draws for an event, ordered by division (`event_group.sort_order`,
 * a null last) then newest first. The model already returns `created_at desc`;
 * the comparator restates it so the order does not depend on the caller.
 */
export function projectDrawList(draws: DrawListRow[]): AppDrawListItem[] {
  return draws
    .filter((d) => isDrawPubliclyVisible(d))
    .sort((a, b) => {
      const sa = a.event_group.sort_order ?? Number.MAX_SAFE_INTEGER;
      const sb = b.event_group.sort_order ?? Number.MAX_SAFE_INTEGER;
      if (sa !== sb) return sa - sb;
      return b.created_at.localeCompare(a.created_at);
    })
    .map((d) => ({
      id: d.id,
      name: d.name,
      status: d.status,
      draw_type: d.draw_type,
      format_type: d.format_type,
      draw_size: d.draw_size,
      division: {
        id: d.event_group.id,
        name: d.event_group.name,
        sort_order: d.event_group.sort_order,
      },
    }));
}

// ---------------------------------------------------------------------------
// Detail — inputs
// ---------------------------------------------------------------------------

type PlayerParts = {
  id: string;
  first_name: string;
  last_name: string;
  /** The player's own `player.alias` column, when the query that produced this
   *  row carries it — optional so older fixtures/rows without it still type-check. */
  alias?: string | null;
};

/** A `draw_match` row as the slim RPC returns it (`get_draw_matches_slim`). */
export type DrawMatchInput = {
  id: string;
  round_id: string | null;
  match_number: number;
  position: number | null;
  status: string;
  outcome?: string | null;
  winner_id: string | null;
  is_bye?: boolean | null;
  is_walkover?: boolean | null;
  player1_is_bye?: boolean | null;
  player2_is_bye?: boolean | null;
  player_1?: PlayerParts | null;
  player_2?: PlayerParts | null;
  score?: unknown;
  event_match_schedule?: {
    scheduled_datetime: string | null;
    court_name?: string | null;
    match?: {
      id: string;
      player_1_games: number | null;
      player_2_games: number | null;
      /** Additive 2026-10-25 (slim RPC): the format played and the retiree, if any. */
      best_of?: number | null;
      retired_player_id?: string | null;
      match_game?: { player_1_score: number; player_2_score: number; status?: string }[] | null;
    } | null;
  } | null;
};

/** `drawModel.getCompleteDrawData(drawId, eventId, event, { slim: true })`. */
export type CompleteDrawInput = {
  draw: {
    id: string;
    name: string;
    draw_type: string;
    format_type: string;
    draw_size: number;
    /** `is_team` marks a team division — its entrants are labelled by `team_name`. */
    event_group: { name: string; is_team?: boolean | null };
  };
  stages: {
    id: string;
    /**
     * The stage's text key (`draw_stage.stage_id`, e.g. `round_robin`,
     * `qualifying_pool_1`) — what `standingsCalculator` stamps on each
     * standings block as `stageId` (`standingsCalculator.ts:490`). Optional
     * because older fixtures omit it; the standings mapping falls back to
     * matching on `id`.
     */
    stage_id?: string | null;
    name: string;
    type: string;
    context: string;
    order: number;
  }[];
  rounds: { id: string; stage_id: string | null; name: string; round_number: number }[];
  matches: DrawMatchInput[];
  seeding: {
    player_id: string;
    seed_number: number | null;
    draw_position: number | null;
    points_at_seeding: number | null;
    points_type: string;
    /** A seat held for a lucky loser (`draw_seeding.is_lucky_loser_slot`); absent in older fixtures. */
    is_lucky_loser_slot?: boolean | null;
  }[];
  /**
   * The division's registrations. `player_id` / `team_name` / `status` are what
   * the team label reads; older fixtures omit them (no label, personal name).
   */
  registrants: {
    player_id?: string;
    team_name?: string | null;
    team_info?: string | null;
    status?: string | null;
    player: PlayerParts & { current_rating?: number | null };
  }[];
  /** `undefined` when the draw has no round-robin stage (drawModel.ts:1216). */
  standings?: {
    stageId: string;
    stageName: string;
    standings: {
      playerId: string;
      position: number;
      wins: number;
      losses: number;
      points: number;
      gamesWon: number;
      gamesLost: number;
      /** futsal-show only (`@/draw/types`, PlayerStanding.draws). */
      draws?: number;
    }[];
  }[];
};

// ---------------------------------------------------------------------------
// Detail — outputs
// ---------------------------------------------------------------------------

/**
 * `team_name` is the team a team-division entrant registered with — `null` for
 * any other division and when the entrant typed none (the stored default is
 * their own name). When set, the app shows it instead of the personal name.
 *
 * `alias` is the player's own `player.alias` column verbatim (`null` when
 * unset). Ladder (product ruling 2026-09-21, cross-repo): team name -> alias
 * -> script-aware personal name -> fallback; the app applies the ladder, this
 * wire just carries every rung.
 */
export type AppDrawPlayer = PlayerParts & {
  seed: number | null;
  team_name: string | null;
  /** The roster as typed, `null` for a non-team division or an entrant without one — never parsed here. */
  team_info: string | null;
  alias: string | null;
  /**
   * `true` for a seat held for a lucky loser: the personal fields above are
   * blank/null (the website shows "Lucky loser", never the withdrawn player),
   * the `id` is unchanged, and the app renders its own localized label. Always
   * present, `false` for every real player.
   */
  is_lucky_loser_placeholder: boolean;
};

export type AppDrawMatch = {
  id: string;
  match_number: number;
  position: number | null;
  player_1: AppDrawPlayer | null;
  player_2: AppDrawPlayer | null;
  player1_is_bye: boolean;
  player2_is_bye: boolean;
  is_bye: boolean;
  is_walkover: boolean;
  status: string;
  outcome: string | null;
  winner_id: string | null;
  games: { p1: number; p2: number } | null;
  match_id: string | null;
  scheduled_at: string | null;
  court_name: string | null;
};

export type AppDrawDetail = {
  draw: {
    id: string;
    name: string;
    draw_type: string;
    format_type: string;
    draw_size: number;
    division_name: string;
  };
  stages: {
    id: string;
    name: string;
    type: string;
    context: string;
    order: number;
    rounds: {
      id: string;
      name: string;
      round_number: number;
      matches: AppDrawMatch[];
    }[];
  }[];
  standings: {
    stage_id: string;
    stage_name: string;
    rows: {
      player_id: string;
      first_name: string;
      last_name: string;
      team_name: string | null;
      team_info: string | null;
      alias: string | null;
      position: number;
      played: number;
      wins: number;
      losses: number;
      points: number;
      games_won: number;
      games_lost: number;
    }[];
  }[];
  seedings: {
    seed_number: number | null;
    draw_position: number | null;
    points_at_seeding: number | null;
    points_type: string;
    player: PlayerParts & {
      team_name: string | null;
      team_info: string | null;
      alias: string | null;
      rating: number | null;
    };
  }[];
  /** Always `false`; retained for clients that still decode it (append-only contract). */
  seedings_hidden: boolean;
  /** Event federation's ratings_enabled (rating gate, 2026-09-23). Additive. */
  ratings_enabled: boolean;
};

// ---------------------------------------------------------------------------
// Detail — projection
// ---------------------------------------------------------------------------

/** One game per pair, credited to whoever scored more; a tie counts for nobody. */
function tallyGames(pairs: [number, number][]): { p1: number; p2: number } {
  let p1 = 0;
  let p2 = 0;
  for (const [a, b] of pairs) {
    if (a > b) p1 += 1;
    else if (b > a) p2 += 1;
  }
  return { p1, p2 };
}

/**
 * Games won per side, from the first source that can answer.
 *
 * 1. The linked match's `player_1_games` / `player_2_games` when both are
 *    recorded — the authoritative finalised count.
 * 2. The slim RPC's `score` when it is a games ARRAY (the legacy shape, slim
 *    migration `20260818070100…:44-45`): one game per element. Element keys
 *    tolerate both spellings exactly as `parseGameScores` does
 *    (`src/draw/core/standingsCalculator.ts:505`).
 * 3. The slim RPC's `score` when it is the live scorer blob,
 *    `{matchState: {games}}` (migration :42-43) — resolved through the shared
 *    `tryResolveAppState` (`src/lib/scorecard/resolve.ts:286`), whose
 *    `player_1_games` / `player_2_games` are the COMPLETED-game tally
 *    (resolve.ts:262-268; the in-progress game is appended to `games` after the
 *    tally, so it never counts). The slim RPC passes only `matchState.games`
 *    through — no `allGameActions` / `gameActions` — which is exactly what the
 *    resolver needs for that tally, so a live bracket match shows its running
 *    score. An unusable blob resolves to null and falls through.
 * 4. The linked match's stored `match_game` rows (migration :66-75), for a
 *    finalised match whose `player_*_games` were never written.
 * 5. Otherwise null — a bye and a walkover carry `games: null`, not `0-0`.
 *
 * Deliberately NOT `parseGameScores` itself: that helper reads `match_game`
 * FIRST, and the slim RPC emits `'[]'::json` (truthy) for a match with no
 * stored games, so a live match would come back 0-0 instead of its running
 * game count. Hence the explicit `length > 0` guard on step 4 too.
 */
function projectGames(m: DrawMatchInput): { p1: number; p2: number } | null {
  const linked = m.event_match_schedule?.match;
  if (linked && linked.player_1_games != null && linked.player_2_games != null) {
    return { p1: linked.player_1_games, p2: linked.player_2_games };
  }

  if (Array.isArray(m.score)) {
    return tallyGames(
      m.score
        .filter((raw): raw is Record<string, unknown> => !!raw && typeof raw === 'object')
        .map((raw) => {
          const g = raw as {
            player1Score?: number;
            player_1_score?: number;
            player2Score?: number;
            player_2_score?: number;
          };
          return [g.player1Score ?? g.player_1_score ?? 0, g.player2Score ?? g.player_2_score ?? 0];
        }),
    );
  }

  if (m.score && typeof m.score === 'object') {
    const resolved = tryResolveAppState(m.score);
    if (resolved) return { p1: resolved.player_1_games, p2: resolved.player_2_games };
  }

  // A retired match's unfinished game (status 'in_progress') is never a game.
  const stored = linked?.match_game?.filter(isCompletedGame);
  if (stored && stored.length > 0) {
    return tallyGames(stored.map((g) => [g.player_1_score, g.player_2_score]));
  }

  return null;
}

/**
 * Seeds come from the same `draw_seeding` rows the `seedings` block is built
 * from; both are always sent. `settings.hide_registrants_from_public` protects
 * the entrant list while registration is open, never a published draw (the
 * organiser is told "Published draws are unaffected"), so no viewer option
 * reaches this projection.
 */
function projectPlayer(
  p: PlayerParts | null | undefined,
  seedByPlayerId: Map<string, number | null>,
  teamEntries: Map<string, TeamEntry>,
  luckyLoserPlayerIds: Set<string>,
): AppDrawPlayer | null {
  if (!p) return null;
  // A held lucky-loser seat is decided before anything else: the withdrawn
  // player's name, seed, team and alias must not reach the wire.
  if (luckyLoserPlayerIds.has(p.id)) {
    return {
      id: p.id,
      first_name: '',
      last_name: '',
      seed: null,
      team_name: null,
      team_info: null,
      alias: null,
      is_lucky_loser_placeholder: true,
    };
  }
  return {
    id: p.id,
    first_name: p.first_name,
    last_name: p.last_name,
    seed: seedByPlayerId.get(p.id) ?? null,
    team_name: teamEntries.get(p.id)?.name ?? null,
    team_info: teamEntries.get(p.id)?.roster ?? null,
    alias: p.alias ?? null,
    is_lucky_loser_placeholder: false,
  };
}

export function projectDrawDetail(
  complete: CompleteDrawInput,
  opts: { ratingsEnabled: boolean } = { ratingsEnabled: true },
): AppDrawDetail {
  const seedByPlayerId = new Map(complete.seeding.map((s) => [s.player_id, s.seed_number]));
  const seedingByPlayerId = new Map(complete.seeding.map((s) => [s.player_id, s]));
  const luckyLoserPlayerIds = getLuckyLoserPlayerIds(complete.seeding);
  const registrantByPlayerId = new Map(complete.registrants.map((r) => [r.player.id, r.player]));
  // Same rule as the web draw page: empty unless this is a team division.
  const teamEntries = buildTeamEntryMap(
    { is_team: complete.draw.event_group.is_team ?? false },
    complete.registrants.map((r) => ({
      player_id: r.player_id ?? r.player.id,
      team_name: r.team_name ?? null,
      team_info: r.team_info ?? null,
      status: r.status,
      player: r.player,
    })),
  );

  // The wire is strictly stage → round → match, so a row with no parent has
  // nowhere to go and is DROPPED: a `draw_match` with a null `round_id`, and a
  // `draw_round` with a null `stage_id` (both columns are nullable —
  // `src/types/supabase.ts:1068`, :1261). An unplaced match has no bracket
  // position to draw, and surfacing it under an invented parent would be worse
  // than omitting it. Stated in MOBILE_CONTRACT.md and pinned by unit tests.
  const matchesByRound = new Map<string, DrawMatchInput[]>();
  for (const m of complete.matches) {
    if (!m.round_id) continue;
    const bucket = matchesByRound.get(m.round_id);
    if (bucket) bucket.push(m);
    else matchesByRound.set(m.round_id, [m]);
  }

  const stages = [...complete.stages]
    .sort((a, b) => a.order - b.order)
    .map((stage) => ({
      id: stage.id,
      name: stage.name,
      type: stage.type,
      context: stage.context,
      order: stage.order,
      rounds: complete.rounds
        .filter((r) => r.stage_id === stage.id)
        .sort((a, b) => a.round_number - b.round_number)
        .map((round) => ({
          id: round.id,
          name: round.name,
          round_number: round.round_number,
          matches: [...(matchesByRound.get(round.id) ?? [])]
            .sort((a, b) => a.match_number - b.match_number)
            .map(
              (m): AppDrawMatch => ({
                id: m.id,
                match_number: m.match_number,
                position: m.position,
                player_1: projectPlayer(
                  m.player_1,
                  seedByPlayerId,
                  teamEntries,
                  luckyLoserPlayerIds,
                ),
                player_2: projectPlayer(
                  m.player_2,
                  seedByPlayerId,
                  teamEntries,
                  luckyLoserPlayerIds,
                ),
                player1_is_bye: m.player1_is_bye ?? false,
                player2_is_bye: m.player2_is_bye ?? false,
                is_bye: m.is_bye ?? false,
                is_walkover: m.is_walkover ?? false,
                status: m.status,
                outcome: m.outcome ?? null,
                winner_id: m.winner_id,
                games: projectGames(m),
                match_id: m.event_match_schedule?.match?.id ?? null,
                scheduled_at: m.event_match_schedule?.scheduled_datetime ?? null,
                court_name: m.event_match_schedule?.court_name ?? null,
              }),
            ),
        })),
    }));

  // A standings block's `stageId` is the stage's TEXT key (`draw_stage.stage_id`,
  // `standingsCalculator.ts:490`), not its row id — but the app matches a
  // block to its stage on `stages[].id` (`draw_stage_body.dart`, `_standings`),
  // so the block is re-keyed by the row id here. Before 2026-09-20 the wire
  // carried the text key and no app version ever rendered a standings card.
  const stageRowIdByKey = new Map<string, string>();
  for (const stage of complete.stages) {
    if (stage.stage_id) stageRowIdByKey.set(stage.stage_id, stage.id);
  }
  const standings = (complete.standings ?? []).map((s) => ({
    stage_id: stageRowIdByKey.get(s.stageId) ?? s.stageId,
    stage_name: s.stageName,
    rows: [...s.standings]
      .sort((a, b) => a.position - b.position)
      .map((row) => {
        // Names come from the group's registrants, not the standing's
        // `playerName`: that field is already formatted (and CJK-ordered) for
        // the web, and the app formats from parts itself.
        const player = registrantByPlayerId.get(row.playerId);
        return {
          player_id: row.playerId,
          first_name: player?.first_name ?? '',
          last_name: player?.last_name ?? '',
          team_name: teamEntries.get(row.playerId)?.name ?? null,
          team_info: teamEntries.get(row.playerId)?.roster ?? null,
          alias: player?.alias ?? null,
          position: row.position,
          played: row.wins + row.losses + (row.draws ?? 0),
          wins: row.wins,
          losses: row.losses,
          points: row.points,
          games_won: row.gamesWon,
          games_lost: row.gamesLost,
        };
      }),
  }));

  // Every ACTIVE registrant (withdrawn ones leave the list, as on the web; the
  // team map above keeps them), merged with its seeding row where there is one — the
  // web's `transformToSeedings`
  // (`…/draw/[drawId]/components/DrawViewClient.tsx:209`), minus the team and
  // photo fields the app does not render. Seeded players first in seed order,
  // then the unseeded alphabetically by the name the app will display.
  const seedings = activeSeedingRegistrants(complete.registrants)
    .map((r) => {
      const s = seedingByPlayerId.get(r.player.id);
      return {
        seed_number: s?.seed_number ?? null,
        draw_position: s?.draw_position ?? null,
        points_at_seeding: s?.points_at_seeding ?? r.player.current_rating ?? null,
        points_type: s?.points_type ?? 'rating',
        player: {
          id: r.player.id,
          first_name: r.player.first_name,
          last_name: r.player.last_name,
          team_name: teamEntries.get(r.player.id)?.name ?? null,
          team_info: teamEntries.get(r.player.id)?.roster ?? null,
          alias: r.player.alias ?? null,
          // The public web seedings list shows the player's CURRENT rating
          // (compact PlayerCard) or nothing; never points_at_seeding, never
          // ranking points. Null when the federation hides ratings.
          rating: opts.ratingsEnabled ? (r.player.current_rating ?? null) : null,
        },
        _sortName: playerModelV2.formatName(r.player.first_name, r.player.last_name),
      };
    })
    .sort((a, b) => {
      const sa = a.seed_number ?? Number.MAX_SAFE_INTEGER;
      const sb = b.seed_number ?? Number.MAX_SAFE_INTEGER;
      if (sa !== sb) return sa - sb;
      return a._sortName.localeCompare(b._sortName);
    })
    .map(({ _sortName: _unused, ...row }) => row);

  return {
    draw: {
      id: complete.draw.id,
      name: complete.draw.name,
      draw_type: complete.draw.draw_type,
      format_type: complete.draw.format_type,
      draw_size: complete.draw.draw_size,
      division_name: complete.draw.event_group.name,
    },
    stages,
    standings,
    seedings,
    // Always false since 2026-09-22 (see the route header); kept on the wire
    // because the contract is append-only and older app builds decode it.
    seedings_hidden: false,
    ratings_enabled: opts.ratingsEnabled,
  };
}
